<template>
  <EditorNodeWrapper
    class="pricing-summary-container"
    @observe-node-visibility="$emit('observe-node-visibility', { ...$event, nodeId: node.id })"
  >
    <div
      v-for="([, { tables, totalPrice }], index) in pricingTablesGroupedByCurrency"
      :key="index"
      class="summary-group"
    >
      <div
        role="table"
        :aria-label="t('pricing_summary_table_number', { number: index + 1 })"
        aria-live="polite"
        class="summary-table"
      >
        <div role="presentation" class="sr-only">
          {{ t('pricing_summary_showing_tables', { count: tables.length }) }}
          {{ t('pricing_summary_with_total_amount_of') }}
          {{ PricingTableHelper.currencyFormat(totalPrice, tables[0].currencySettings) }}
        </div>
        <div role="rowgroup">
          <div role="row" class="header-row">
            <div role="columnheader" scope="col" class="name-column">
              {{ node.labels?.nameColumn || t('item') }}
            </div>
            <div role="columnheader" scope="col" class="price-column">
              {{ node.labels?.priceColumn || t('cpq_pricing_summary_total') }}
            </div>
          </div>
        </div>
        <div role="rowgroup">
          <div v-for="table in tables" :key="table.id" role="row" class="row">
            <div role="cell" class="name-column">
              {{ table.displayName || table.name || t('untitled-table') }}
            </div>
            <div role="cell" class="price-column">
              <span class="sr-only">{{ getTablePriceAriaLabelComputed(table) }}</span>
              <span aria-hidden="true">{{
                PricingTableHelper.currencyFormat(table.value, table.currencySettings)
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!hideTotalPrice" class="summary-container">
        <div class="value-label" aria-hidden="true">
          {{ node.labels?.totalPrice || t('cpq_pricing_summary_total_sum') }}
        </div>
        <div class="value">
          <span class="sr-only">{{
            getTotalPriceAriaLabelComputed(totalPrice, tables[0].currencySettings)
          }}</span>
          <span aria-hidden="true">{{
            PricingTableHelper.currencyFormat(totalPrice, tables[0].currencySettings)
          }}</span>
        </div>
      </div>
    </div>
    <div v-if="pricingTablesGroupedByCurrency.size === 0" class="summary-group">
      <div role="table" :aria-label="t('empty_pricing_summary')" class="summary-table">
        <div role="presentation" class="sr-only">{{ t('no_pricing_tables_available') }}</div>
        <div role="rowgroup">
          <div role="row" class="header-row">
            <div role="columnheader" scope="col" class="name-column">
              {{ node.labels?.nameColumn || t('item') }}
            </div>
            <div role="columnheader" scope="col" class="price-column">
              {{ node.labels?.priceColumn || t('cpq_pricing_summary_total') }}
            </div>
          </div>
        </div>
        <div role="rowgroup">
          <div role="row">
            <div role="cell" class="empty-message">
              {{ t('no_pricing_data_available') }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="!hideTotalPrice" class="summary-container">
        <div class="value-label" aria-hidden="true">
          {{ node.labels?.totalPrice || t('cpq_pricing_summary_total_sum') }}
        </div>
        <div class="value">
          <span class="sr-only">{{ getEmptyTotalPriceAriaLabel(firstTableCurrency) }}</span>
          <span aria-hidden="true">{{ 0 }}</span>
        </div>
      </div>
    </div>
  </EditorNodeWrapper>
</template>
<script lang="ts" setup>
import type { PricingSummary, Context, PricingTable } from '@getaccept/editor-lib';
import {
  PricingSummaryHelper,
  PricingTableHelper,
  hasUsedPricingRows,
} from '@getaccept/editor-lib';
import { computed } from 'vue';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import { storeToRefs } from 'pinia';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import {
  getEmptyTotalPriceAriaLabel,
  createTablePriceAriaLabel,
  createTotalPriceAriaLabelForSummary,
} from '@getaccept/editor-lib/src/helpers/pricing-table/pricing-table-accessibility.helper';
import { useEditorViewContentStore } from '../store/editor-view-content.store';
import EditorNodeWrapper from './EditorNodeWrapper.vue';

const props = defineProps<{
  node: PricingSummary;
  context: Context;
  recipientInput: EditorRecipientInput[];
}>();

defineEmits<{
  'observe-node-visibility': [
    {
      nodeId: string;
      visible: boolean;
    },
  ];
}>();

const editorViewContentStore = useEditorViewContentStore();
const { allPricingTables } = storeToRefs(editorViewContentStore);

const usedPricingTableSections = (node: PricingTable) =>
  node.pricingTableSections.filter(hasUsedPricingRows).length > 0;

const pricingTablesGroupedByCurrency = computed(() =>
  PricingSummaryHelper.pricingTablesGroupedByCurrency(
    allPricingTables.value.filter(usedPricingTableSections),
    props.recipientInput,
    props.node.tables
  )
);

const hideTotalPrice = computed(() => props.node.pricingSummarySettings?.hideTotalPrice);

const firstTableCurrencySettings = computed(() => {
  const firstGroup = pricingTablesGroupedByCurrency.value.values().next().value;
  return firstGroup?.tables?.[0]?.currencySettings;
});

const firstTableCurrency = computed(
  () => firstTableCurrencySettings.value?.formatOptions?.currency || ''
);

const getTablePriceAriaLabelComputed = (table: {
  displayName?: string;
  name?: string;
  value?: string;
  currencySettings?: any;
}) => createTablePriceAriaLabel(table);

const getTotalPriceAriaLabelComputed = (totalPrice: string | number, currencySettings: any) =>
  createTotalPriceAriaLabelForSummary(totalPrice, currencySettings);
</script>
<style scoped>
@import '@getaccept/editor-lib/src/css/editor-node-pricing-summary.css';
</style>
